import React, { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import axios from 'axios';

const StudentsAdmin = () => {
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [userToUpdateRole, setUserToUpdateRole] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [roleUpdateLoading, setRoleUpdateLoading] = useState(false);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axios.get('/api/admin/students');
            setStudents(data.students || []);
        } catch (e) {
            setError('Failed to load students');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentResults = async (studentId) => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axios.get('/api/tests/results', { params: { userId: studentId } });
            setResults(data.results || []);
        } catch (e) {
            setError('Failed to load student results');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleString();

    const handleRoleChange = (user) => {
        setUserToUpdateRole(user);
        setNewRole(user.role);
        setRoleDialogOpen(true);
    };

    const updateUserRole = async () => {
        if (!userToUpdateRole || !newRole) return;

        setRoleUpdateLoading(true);
        setError('');
        try {
            const { data } = await axios.patch(`/api/admin/users/${userToUpdateRole.id}/role`, {
                role: newRole
            });

            // Update the user in the students list
            setStudents(prev => prev.map(s =>
                s.id === userToUpdateRole.id
                    ? { ...s, role: newRole }
                    : s
            ));

            setRoleDialogOpen(false);
            setUserToUpdateRole(null);
            setNewRole('');
        } catch (e) {
            setError(e.response?.data?.error || 'Failed to update user role');
        } finally {
            setRoleUpdateLoading(false);
        }
    };

    const closeRoleDialog = () => {
        setRoleDialogOpen(false);
        setUserToUpdateRole(null);
        setNewRole('');
    };

    if (loading && students.length === 0 && results.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4">Users Management</Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Browse all users and manage their roles and test activity.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">All Users</Typography>
                            <Button onClick={fetchStudents}>Refresh</Button>
                        </Box>
                        <List>
                            {students.map((s, idx) => (
                                <React.Fragment key={s.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body1">{s.name}</Typography>
                                                    <Chip
                                                        label={s.role}
                                                        size="small"
                                                        color={s.role === 'admin' ? 'primary' : 'default'}
                                                        variant={s.role === 'admin' ? 'filled' : 'outlined'}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {s.email}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {s.testCount} tests completed
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleRoleChange(s)}
                                            >
                                                Change Role
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={async () => { setSelectedStudent(s); await fetchStudentResults(s.id); }}
                                            >
                                                View Tests
                                            </Button>
                                        </Box>
                                    </ListItem>
                                    {idx < students.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2, minHeight: 300 }}>
                        <Typography variant="h6" gutterBottom>
                            {selectedStudent ? `${selectedStudent.name}'s Test Results` : 'Select a user to view their test results'}
                        </Typography>

                        {selectedStudent && results.length === 0 && (
                            <Typography color="text.secondary">No test results found for this user.</Typography>
                        )}

                        {selectedStudent && results.length > 0 && (
                            <Grid container spacing={2}>
                                {results.map((r) => (
                                    <Grid item xs={12} key={r.id}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography variant="subtitle1">{r.testName || 'Untitled Test'}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{formatDate(r.submittedAt)}</Typography>
                                                    </Box>
                                                    <Chip label={`${r.score}%`} color={r.score >= 80 ? 'success' : r.score >= 60 ? 'warning' : 'error'} />
                                                </Box>
                                                <Box mt={1}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {r.correctAnswers}/{r.totalQuestions} correct
                                                    </Typography>
                                                </Box>
                                                <Box mt={1}>
                                                    <Typography variant="body2">Answers: {r.answers.join(', ')}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Role Change Dialog */}
            <Dialog open={roleDialogOpen} onClose={closeRoleDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Change User Role
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Change role for: <strong>{userToUpdateRole?.name}</strong> ({userToUpdateRole?.email})
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel id="role-select-label">Role</InputLabel>
                        <Select
                            labelId="role-select-label"
                            value={newRole}
                            label="Role"
                            onChange={(e) => setNewRole(e.target.value)}
                            disabled={roleUpdateLoading}
                        >
                            <MenuItem value="student">Student</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRoleDialog} disabled={roleUpdateLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={updateUserRole}
                        variant="contained"
                        disabled={roleUpdateLoading || !newRole || newRole === userToUpdateRole?.role}
                    >
                        {roleUpdateLoading ? <CircularProgress size={20} /> : 'Update Role'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default StudentsAdmin;


